import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/layout/page-header";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { ProdutorShell } from "@/components/mercado/produtor-shell";
import { StatusChip } from "@/components/mercado/status-chip";
import { PainelChamada } from "@/components/mercado/painel-chamada";
import { rotuloVariedade } from "@/components/mercado/bloco-oferta";
import { useAuth } from "@/lib/auth-context";
import { useOferta } from "@/hooks/use-ofertas";
import { fmtDiaCurto, fmtKgBR } from "@/lib/formato-mercado";

/**
 * `/mercado/ofertas/:id` — a oferta publicada, e logo abaixo COMO MANDAR.
 *
 * ═══ ESTA TELA EXISTE PARA DESTRAVAR O COMEÇO ═══════════════════════════════
 *
 * O formulário pedia o destinatário antes de existir a carga: a primeira
 * decisão da tela era "para quem enviar", e quem só tem o grupo da praça
 * parava ali. Publicar virou o corte — antes dele, a carga; depois dele, o
 * envio. A mesma informação, na ordem em que a cabeça do produtor a produz.
 *
 * O cabeçalho repete a carga em uma linha ("OF-0001 · Prata, cerca de 3.000
 * kg, 85% de 1ª · a partir de ter 15/09") porque quem chega aqui vindo da
 * lista precisa saber QUAL oferta está mandando antes de tocar em Enviar.
 */
export default function OfertaDetalhe() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const ativo = user?.orgTipo !== "COMPRADOR";
  const { data: oferta, isLoading, isError } = useOferta(ativo ? id : undefined);

  if (isLoading) {
    return <ProdutorShell><PageSkeleton variante="painel" /></ProdutorShell>;
  }

  if (isError || !oferta) {
    return (
      <ProdutorShell>
        <div className="rounded-lg border border-dashed bg-card/60 px-4 py-10 text-center space-y-2" data-testid="oferta-nao-encontrada">
          <p className="text-sm font-medium">Esta oferta não está mais disponível.</p>
          <Button variant="outline" size="sm" onClick={() => setLocation("/mercado/ofertas")}>
            Ver minhas ofertas
          </Button>
        </div>
      </ProdutorShell>
    );
  }

  // A carga em UMA linha, na mesma ordem da chamada: variedade, volume, % e
  // quando. A Nanica não cita a % — a praça publica uma Nanica só.
  const carga = [
    rotuloVariedade(oferta.variedade),
    `cerca de ${fmtKgBR(oferta.volumeKg)}`,
    oferta.variedade === "NANICA" ? null : `${Math.round(oferta.pctPrimeira)}% de 1ª`,
  ].filter(Boolean).join(", ");

  return (
    <ProdutorShell>
      <PageHeader
        titulo={`${oferta.identificador} · ${carga}`}
        descricao={`A partir de ${fmtDiaCurto(oferta.disponivelEm)} · praça ${oferta.pracaNome}`}
        empilharQuandoEstreito
        acoesSecundarias={
          <StatusChip kind={oferta.status === "NA_PRACA" ? "info" : oferta.status === "FECHADA" ? "pago" : "aberta"}>
            {oferta.status === "NA_PRACA" ? "Na praça" : oferta.status === "FECHADA" ? "Fechada" : oferta.status === "RASCUNHO" ? "Rascunho" : "Encerrada"}
          </StatusChip>
        }
        acao={
          <Button variant="outline" onClick={() => setLocation("/mercado/ofertas")} data-testid="button-ver-ofertas">
            Ver em Ofertas
          </Button>
        }
      />

      <div className="max-w-[720px]">
        {/* Rascunho não tem o que enviar: o link público só nasce na
            publicação, e um painel vazio aqui prometeria um envio que não
            existe. */}
        {oferta.status === "RASCUNHO" ? (
          <div className="rounded-lg border border-dashed px-3 py-6 text-center space-y-2" data-testid="oferta-rascunho">
            <p className="text-sm">Este rascunho ainda não está na praça.</p>
            <p className="text-xs text-muted-foreground">Coloque na praça em Ofertas para gerar o link e mandar a chamada.</p>
          </div>
        ) : (
          <PainelChamada ofertaId={oferta.id} />
        )}
      </div>
    </ProdutorShell>
  );
}
